import type { StageStep } from "../components";
import { t } from "../i18n/t";
import type { MigrationState } from "../api/types";

/**
 * §B1.4: **the stage machine drives navigation.** Every `MigrationState` maps to the screen that owns it, and the
 * four-step nav is derived from the same map — so a redirect and a breadcrumb can never disagree.
 *
 * Pure functions only: no JSX, no fetching, no router imports. `<StageNav>` (06a) renders what `stageSteps`
 * returns; `router.tsx` redirects with what `routeForState` returns; tasks 07, 08 and 09 gate their read-only
 * banner on `isStageActive`.
 */

export type Stage = "connect" | "scope" | "review" | "dry-run" | "status";

/** The five steps the redesign's rail renders (guide §3): status IS a step now ("05 · Status" on the
 * rail), reached by everything `STAGE_OF_STATE` maps to it. */
export const NAV_STAGES: readonly Stage[] = ["connect", "scope", "review", "dry-run", "status"];

const STAGE_OF_STATE: Readonly<Record<MigrationState, Stage>> = {
  draft: "connect",
  connecting: "connect",
  profiling: "scope",
  scoping: "scope",
  awaiting_scope_signoff: "scope",
  extracting: "review",
  mapping: "review",
  awaiting_mapping_review: "review",
  transforming: "dry-run",
  dry_running: "dry-run",
  awaiting_conversion_signoff: "dry-run",
  loading: "status",
  verifying: "status",
  hypercare: "status",
  locked: "status",
  failed: "status",
  cancelled: "status",
  rolling_back: "status",
  rolled_back: "status",
};

export function stageForState(state: MigrationState): Stage {
  return STAGE_OF_STATE[state];
}

export function pathForStage(migrationId: string, stage: Stage): string {
  // §B1.3: the connect step has no per-migration route — a migration exists only once both connections do.
  if (stage === "connect") return "/migrations/new";
  return `/migrations/${migrationId}/${stage}`;
}

export function routeForState(migrationId: string, state: MigrationState): string {
  return pathForStage(migrationId, stageForState(state));
}

export function stageLabel(stage: Stage): string {
  if (stage === "connect") return t("stage.connect");
  if (stage === "scope") return t("stage.scope");
  if (stage === "review") return t("stage.review");
  if (stage === "dry-run") return t("stage.dry_run");
  return t("stage.status");
}

/**
 * §B1.4: "a screen visited out of order renders read-only with a 'this step is not active yet' banner rather than
 * 404ing — an accountant re-reading a signed scope is a normal act." A stage the migration has passed is *not*
 * out of order: it is history, and history is readable.
 */
export function isStageActive(state: MigrationState, stage: Stage): boolean {
  return stageForState(state) === stage;
}

export function isStagePast(state: MigrationState, stage: Stage): boolean {
  const current = NAV_STAGES.indexOf(stageForState(state));
  const asked = NAV_STAGES.indexOf(stage);
  if (asked === -1) return false;
  // `status` (current === -1) is reached only after every step, so every step is past.
  return current === -1 ? true : asked < current;
}

/**
 * §3's rail, five steps. `stoppedStage` is the stage a failed/cancelled migration stopped AT (the failure
 * event's own `retry_target`, mapped back through the same state machine) — its step reads "stopped" in the
 * rail (StatusFailed.html), later steps are upcoming, earlier ones done.
 */
/**
 * Guide §6's state → pill tone map, shared by the migrations table and the migration-screen header:
 * in-progress states are `run`, the awaiting-sign-off states are `warn`, the failed family is `fail`,
 * `hypercare` is `pass`, and `draft`/`locked` are deliberately grey (`neutral` = the missing tone).
 */
export function statePillTone(state: MigrationState): "neutral" | "pass" | "fail" | "warn" | "run" {
  if (state === "hypercare") return "pass";
  if (state === "failed" || state === "cancelled" || state === "rolled_back" || state === "rolling_back") {
    return "fail";
  }
  if (
    state === "awaiting_scope_signoff" ||
    state === "awaiting_mapping_review" ||
    state === "awaiting_conversion_signoff"
  ) {
    return "warn";
  }
  if (state === "draft" || state === "locked") return "neutral";
  return "run";
}

/**
 * Extraction and mapping run on the Review route (so the redirect and the "opens when extraction finishes"
 * page stay where they were), but nothing is reviewable until `awaiting_mapping_review`: no mapping version
 * exists. The rail therefore reads them as step 02 still in progress, and step 03 as not reached.
 */
export function isMappingNotReady(state: MigrationState): boolean {
  return state === "extracting" || state === "mapping";
}

export function stageSteps(
  migrationId: string,
  state: MigrationState,
  stoppedStage?: Stage | null,
  paused = false,
): StageStep[] {
  const notReady = isMappingNotReady(state);
  const current = notReady ? "scope" : stageForState(state);
  const currentIndex = NAV_STAGES.indexOf(current);
  const stopIndex =
    stoppedStage === undefined || stoppedStage === null ? -1 : NAV_STAGES.indexOf(stoppedStage);
  return NAV_STAGES.map((stage, index) => {
    const stepState: StageStep["state"] =
      stopIndex !== -1
        ? index === stopIndex
          ? "stopped"
          : index < stopIndex
            ? "done"
            : "upcoming"
        : currentIndex !== -1 && index === currentIndex
          ? "current"
          : currentIndex === -1 || index <= currentIndex
            ? "done"
            : "upcoming";
    const note =
      notReady && stepState === "current"
        ? paused
          ? t("stage.note_paused")
          : state === "mapping"
            ? t("stage.note_mapping")
            : t("stage.note_extracting")
        : undefined;
    return {
      id: stage,
      label: stageLabel(stage),
      href: stepState === "upcoming" ? null : pathForStage(migrationId, stage),
      state: stepState,
      ...(note === undefined ? {} : { note }),
    };
  });
}
